import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import GenericModal from '../GenericModal';
import { startOfToday, endOfToday, startOfYesterday, endOfYesterday, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval, parseISO, format } from 'date-fns';
import useImportToolUsage from '../../utilities/useImportToolUsage';

const pastelColors = [
  '#7FB3D5', '#76D7C4', '#F8C471', '#F1948A', '#BB8FCE',
  '#F7DC6F', '#73C6B6', '#C39BD3', '#82E0AA', '#F5B7B1',
];

export default function ImportToolUsage({ data }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Sorting state for user table
  const [sortConfig, setSortConfig] = useState({ key: 'userName', direction: 'asc' });
  const [dateFilter, setDateFilter] = useState('all');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  function getDateStrings() {
    const now = new Date();
    switch (dateFilter) {
      case 'today':
        return { startDate: format(startOfToday(), 'yyyy-MM-dd'), endDate: format(endOfToday(), 'yyyy-MM-dd') };
      case 'yesterday':
        return { startDate: format(startOfYesterday(), 'yyyy-MM-dd'), endDate: format(endOfYesterday(), 'yyyy-MM-dd') };
      case 'thisWeek':
        return { startDate: format(startOfWeek(now), 'yyyy-MM-dd'), endDate: format(endOfWeek(now), 'yyyy-MM-dd') };
      case 'thisMonth':
        return { startDate: format(startOfMonth(now), 'yyyy-MM-dd'), endDate: format(endOfMonth(now), 'yyyy-MM-dd') };
      case 'custom':
        if (customRange.start && customRange.end) {
          return { startDate: customRange.start, endDate: customRange.end };
        }
        return {};
      default:
        return {};
    }
  }

  const { startDate, endDate } = getDateStrings();
  const { data: fetchedData, loading, error } = useImportToolUsage({ startDate, endDate });

  // Sorting logic for users table
  const sortedUsers = React.useMemo(() => {
    if (!fetchedData || !fetchedData.users) return [];
    const users = fetchedData.users;
    const sorted = [...users];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      // Special handling for nested or computed fields
      if (sortConfig.key === 'importTypes') {
        aValue = Object.keys(a.importTypes).length;
        bValue = Object.keys(b.importTypes).length;
      }
      if (sortConfig.key === 'firstImport' || sortConfig.key === 'lastImport') {
        aValue = a[sortConfig.key] ? new Date(a[sortConfig.key]) : new Date(0);
        bValue = b[sortConfig.key] ? new Date(b[sortConfig.key]) : new Date(0);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [fetchedData, sortConfig]);

  // Date filter logic
  function getDateInterval() {
    const now = new Date();
    switch (dateFilter) {
      case 'today':
        return { start: startOfToday(), end: endOfToday() };
      case 'yesterday':
        return { start: startOfYesterday(), end: endOfYesterday() };
      case 'thisWeek':
        return { start: startOfWeek(now), end: endOfWeek(now) };
      case 'thisMonth':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'custom':
        if (customRange.start && customRange.end) {
          return { start: new Date(customRange.start), end: new Date(customRange.end + 'T23:59:59') };
        }
        return null;
      default:
        return null;
    }
  }

  const dateInterval = getDateInterval();

  // Filter users by date interval
  const filteredUsers = React.useMemo(() => {
    if (!fetchedData?.users) return [];
    if (!dateInterval) return fetchedData.users;
    return fetchedData.users.map(user => {
      // Filter importTypes by date
      const filteredImportTypes = {};
      Object.entries(user.importTypes).forEach(([type, info]) => {
        const firstUsed = info.firstUsed ? parseISO(info.firstUsed) : null;
        const lastUsed = info.lastUsed ? parseISO(info.lastUsed) : null;
        if (
          (firstUsed && isWithinInterval(firstUsed, dateInterval)) ||
          (lastUsed && isWithinInterval(lastUsed, dateInterval))
        ) {
          filteredImportTypes[type] = info;
        }
      });
      // Only include users with at least one import in range
      const totalImports = Object.values(filteredImportTypes).reduce((sum, t) => sum + (t.usageCount || 0), 0);
      if (totalImports > 0) {
        return {
          ...user,
          importTypes: filteredImportTypes,
          totalImports,
          firstImport: user.firstImport,
          lastImport: user.lastImport,
        };
      }
      return null;
    }).filter(Boolean);
  }, [fetchedData, dateInterval]);

  // Optionally, recalculate summary and importTypes from filteredUsers
  // For now, use original summary/importTypes for simplicity

  if (!fetchedData || !fetchedData.users || !fetchedData.summary) {
    return (
      <div className="text-center text-gray-100 py-8">
        No import tool usage data available.
        {fetchedData && <div className="text-xs mt-2">Debug: {JSON.stringify(fetchedData)}</div>}
      </div>
    );
  }

  const { users, importTypes, summary } = fetchedData;

  // Prepare data for different chart types
  const importTypeData = importTypes.map((type, index) => ({
    name: type,
    users: summary.importTypeBreakdown[type]?.usersCount || 0,
    totalUsage: summary.importTypeBreakdown[type]?.totalUsage || 0,
    avgUsage: parseFloat(summary.importTypeBreakdown[type]?.averageUsagePerUser || 0),
    color: pastelColors[index % pastelColors.length]
  }));

  const userActivityData = filteredUsers
    .filter(user => user.totalImports > 0)
    .sort((a, b) => b.totalImports - a.totalImports)
    .slice(0, 10)
    .map((user, index) => ({
      name: user.userName,
      totalImports: user.totalImports,
      importTypes: Object.keys(user.importTypes).length,
      color: pastelColors[index % pastelColors.length]
    }));

  const pieData = importTypes.map((type, index) => ({
    name: type,
    value: summary.importTypeBreakdown[type]?.totalUsage || 0,
    color: pastelColors[index % pastelColors.length]
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#1f2937',
          border: '1px solid #4b5563',
          borderRadius: '6px',
          padding: '6px 8px',
          fontSize: '0.75rem',
          color: '#f9fafb',
        }}>
          <p style={{ color: '#f9fafb', fontWeight: 500 }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color || '#f3f4f6', fontSize: '0.75rem', margin: 0 }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Styled sort icon
  const SortIcon = ({ active, direction }) => (
    <span style={{ marginLeft: 4, fontSize: '1em', color: active ? '#60a5fa' : '#6b7280', verticalAlign: 'middle' }}>
      {direction === 'asc' ? '▲' : '▼'}
    </span>
  );

  return (
    <div className="space-y-6">
      {/* Date Filter UI */}
      <div className="flex items-center space-x-4 mb-4">
        <div className="relative inline-block">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
            {/* Calendar SVG */}
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M16 3v4M8 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M3 9h18" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </span>
          <select
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="bg-gray-800 text-white rounded px-9 py-2 appearance-none"
            style={{ paddingLeft: '2.5rem' }}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        {dateFilter === 'custom' && (
          <>
            <input
              type="date"
              value={customRange.start}
              onChange={e => setCustomRange(r => ({ ...r, start: e.target.value }))}
              className="bg-gray-800 text-white rounded px-2 py-1"
            />
            <span className="text-gray-300">to</span>
            <input
              type="date"
              value={customRange.end}
              onChange={e => setCustomRange(r => ({ ...r, end: e.target.value }))}
              className="bg-gray-800 text-white rounded px-2 py-1"
            />
          </>
        )}
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          {
            label: 'Total Users',
            value: summary.totalUsers,
            color: pastelColors[0],
          },
          {
            label: 'Total Imports',
            value: summary.totalImports,
            color: pastelColors[1],
          },
          {
            label: 'Import Types',
            value: importTypes.length,
            color: pastelColors[2],
          },
          {
            label: 'Avg Imports/User',
            value: summary.totalUsers > 0 ? (summary.totalImports / summary.totalUsers).toFixed(1) : 0,
            color: pastelColors[3],
          },
        ].map((card, i) => (
          <div
            key={card.label}
            style={{
              backgroundColor: card.color + '22', // pastel color with opacity
              border: `1.5px solid ${card.color}55`,
              borderRadius: '0.5rem',
              padding: '1rem',
            }}
          >
            <h3 style={{ color: card.color, fontSize: '0.95rem', fontWeight: 500 }}>{card.label}</h3>
            <p style={{ color: card.color, fontSize: '2rem', fontWeight: 700 }}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-800 rounded-lg p-1">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'users', label: 'User Breakdown' },
          { id: 'types', label: 'Import Types' },
          { id: 'activity', label: 'Top Users' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-hs-blue text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-gray-900 rounded-lg p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
             
             {/* Pie Chart for Import Distribution */}
             <div className="h-[32rem]">
              <h3 className="text-white text-lg font-semibold mb-4">Import Type Distribution</h3>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                  <Legend wrapperStyle={{ paddingTop: 10, color: '#e5e7eb' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Import Type Usage Bar Chart */}
            <div className="h-80">
              <h3 className="text-white text-lg font-semibold mb-4">Import Type Usage</h3>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={importTypeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2f2f2f" />
                  <XAxis dataKey="name" stroke="#d1d5db" tick={{ fill: '#d1d5db', fontSize: 12 }} axisLine={{ stroke: '#4b5563' }} />
                  <YAxis stroke="#d1d5db" tick={{ fill: '#d1d5db', fontSize: 12 }} axisLine={{ stroke: '#4b5563' }} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                  <Legend wrapperStyle={{ paddingTop: 10, color: '#e5e7eb' }} />
                  {['totalUsage', 'users'].map((key, i) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={key === 'totalUsage' ? 'Total Usage' : 'Users'}
                      fill={pastelColors[i % pastelColors.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

           
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <h3 className="text-white text-lg font-semibold mb-4">User Import Activity</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-300">
                <thead className="text-xs text-gray-200 uppercase bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 cursor-pointer select-none" onClick={() => setSortConfig(prev => ({ key: 'userName', direction: prev.key === 'userName' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      User
                      <SortIcon active={sortConfig.key === 'userName'} direction={sortConfig.key === 'userName' ? sortConfig.direction : 'asc'} />
                    </th>
                    <th className="px-6 py-3 cursor-pointer select-none" onClick={() => setSortConfig(prev => ({ key: 'userEmail', direction: prev.key === 'userEmail' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      Email
                      <SortIcon active={sortConfig.key === 'userEmail'} direction={sortConfig.key === 'userEmail' ? sortConfig.direction : 'asc'} />
                    </th>
                    <th className="px-6 py-3 cursor-pointer select-none" onClick={() => setSortConfig(prev => ({ key: 'totalImports', direction: prev.key === 'totalImports' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      Total Imports
                      <SortIcon active={sortConfig.key === 'totalImports'} direction={sortConfig.key === 'totalImports' ? sortConfig.direction : 'asc'} />
                    </th>
                    <th className="px-6 py-3 cursor-pointer select-none" onClick={() => setSortConfig(prev => ({ key: 'importTypes', direction: prev.key === 'importTypes' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      Import Types
                      <SortIcon active={sortConfig.key === 'importTypes'} direction={sortConfig.key === 'importTypes' ? sortConfig.direction : 'asc'} />
                    </th>
                    <th className="px-6 py-3 cursor-pointer select-none" onClick={() => setSortConfig(prev => ({ key: 'firstImport', direction: prev.key === 'firstImport' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      First Import
                      <SortIcon active={sortConfig.key === 'firstImport'} direction={sortConfig.key === 'firstImport' ? sortConfig.direction : 'asc'} />
                    </th>
                    <th className="px-6 py-3 cursor-pointer select-none" onClick={() => setSortConfig(prev => ({ key: 'lastImport', direction: prev.key === 'lastImport' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      Last Import
                      <SortIcon active={sortConfig.key === 'lastImport'} direction={sortConfig.key === 'lastImport' ? sortConfig.direction : 'asc'} />
                    </th>
                    <th className="px-6 py-3">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user) => (
                    <tr key={user.userId} className="border-b border-gray-700 hover:bg-gray-800">
                      <td className="px-6 py-4 font-medium text-white">{user.userName}</td>
                      <td className="px-6 py-4 text-gray-100">{user.userEmail}</td>
                      <td className="px-6 py-4 text-gray-100">{user.totalImports}</td>
                      <td className="px-6 py-4 text-gray-100">{Object.keys(user.importTypes).length}</td>
                      <td className="px-6 py-4 text-gray-100">
                        {user.firstImport ? new Date(user.firstImport).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-gray-100">
                        {user.lastImport ? new Date(user.lastImport).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => { setSelectedUser(user.userId); setModalOpen(true); }}
                          className="text-blue-300 hover:text-blue-200 text-xs"
                        >
                          Show Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* User Details Modal */}
            <GenericModal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
              {selectedUser && (
                <div className="mt-2 w-full">
                  <h4 className="text-gray-100 font-semibold mb-3 break-words whitespace-normal">Import Type Breakdown</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full min-w-0">
                    {filteredUsers.find(u => u.userId === selectedUser)?.importTypes && 
                      Object.entries(filteredUsers.find(u => u.userId === selectedUser).importTypes).map(([type, data]) => (
                        <div key={type} className="bg-gray-900 rounded p-3 w-full min-w-0 break-words whitespace-normal">
                          <h5 className="text-hs-blue font-medium break-words whitespace-normal">{type}</h5>
                          <p className="text-gray-300 text-sm break-words whitespace-normal">Usage: {data.usageCount}</p>
                          <p className="text-gray-500 text-xs break-words whitespace-normal">
                            First: {new Date(data.firstUsed).toLocaleDateString()}
                          </p>
                          <p className="text-gray-500 text-xs break-words whitespace-normal">
                            Last: {new Date(data.lastUsed).toLocaleDateString()}
                          </p>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </GenericModal>
          </div>
        )}

        {activeTab === 'types' && (
          <div>
            <h3 className="text-white text-lg font-semibold mb-4">Import Type Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {importTypes.map((type, index) => {
                const typeData = summary.importTypeBreakdown[type];
                return (
                  <div key={type} className="bg-gray-800 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-3">{type}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-100">Users:</span>
                        <span className="text-white font-medium">{typeData?.usersCount || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-100">Total Usage:</span>
                        <span className="text-white font-medium">{typeData?.totalUsage || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-100">Avg per User:</span>
                        <span className="text-white font-medium">{typeData?.averageUsagePerUser || 0}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            <h3 className="text-white text-lg font-semibold mb-4">Top Users by Import Activity</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={userActivityData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2f2f2f" />
                  <XAxis dataKey="name" stroke="#d1d5db" tick={{ fill: '#d1d5db', fontSize: 12 }} axisLine={{ stroke: '#4b5563' }} />
                  <YAxis stroke="#d1d5db" tick={{ fill: '#d1d5db', fontSize: 12 }} axisLine={{ stroke: '#4b5563' }} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                  <Legend wrapperStyle={{ paddingTop: 10, color: '#e5e7eb' }} />
                  {['totalImports', 'importTypes'].map((key, i) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={key === 'totalImports' ? 'Total Imports' : 'Import Types Used'}
                      fill={pastelColors[i % pastelColors.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 